import 'ol/ol.css';
import * as React from 'react';
import Map from 'ol/Map';
import Projection from 'ol/proj/Projection';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import TileGrid from 'ol/tilegrid/TileGrid';
import XYZ from 'ol/source/XYZ';
import { Auth, IAuthToken } from '@shared/Auth';
import { tileLoadFunction } from '@shared/openlayers/tile-layers/TileLoader';

type PdfTileLevel = {
    cols: number;
    rows: number;
    width: number;
    height: number;
};

type PdfTileMetadata = {
    rasterization_needed?: boolean;
    levels?: Record<string, PdfTileLevel>;
};

type PdfRasterViewerProps = {
    tileUrl: string;
    height?: string;
};

type RasterConfig = {
    projection: Projection;
    tileGrid: TileGrid | null;
    maxZoom: number;
    center: [number, number];
};

const getMetadataUrl = (tileUrl: string): string | null => {
    if (!tileUrl) {
        return null;
    }

    const templateSuffix = '/tiles/{z}/{x}/{y}.webp';
    const encodedTemplateSuffix = '/tiles/%7Bz%7D/%7Bx%7D/%7By%7D.webp';

    if (tileUrl.includes(templateSuffix)) {
        return tileUrl.replace(templateSuffix, '/metadata.json');
    }

    if (tileUrl.includes(encodedTemplateSuffix)) {
        return tileUrl.replace(encodedTemplateSuffix, '/metadata.json');
    }

    return null;
};

const fetchTileMetadata = async (tileUrl: string): Promise<PdfTileMetadata | null> => {
    const metadataUrl: string | null = getMetadataUrl(tileUrl);
    if (!metadataUrl) {
        return null;
    }

    try {
        const token: IAuthToken = await Auth.getToken();
        const response: Response = await fetch(metadataUrl, {
            headers: {
                authorization: `Bearer ${token.accessToken}`,
            },
        });
        if (!response.ok) {
            return null;
        }

        const metadata: PdfTileMetadata = await response.json();
        if (!metadata.levels || Object.keys(metadata.levels).length === 0) {
            return null;
        }

        return metadata;
    } catch {
        return null;
    }
};

/**
 * Builds OpenLayers raster configuration for PDF tile pyramids.
 *
 * The backend outputs tiles in a custom pixel-space pyramid (`tiles/{z}/{x}/{y}.webp`) with
 * dimensions per zoom level in `metadata.levels`. This converts that metadata into a
 * projection + tile grid that OpenLayers can request correctly.
 *
 * Coordinate system used:
 * - Origin at top-left of the PDF image
 * - X increases to the right
 * - Y increases downward (represented as negative Y in extent/center for OpenLayers)
 *
 * Fallback behavior:
 * - If metadata is missing/invalid, returns a basic EPSG:3857 config with no custom tile grid.
 */
const buildRasterConfig = (metadata: PdfTileMetadata | null): RasterConfig => {
    if (!metadata?.levels) {
        return {
            projection: new Projection({ code: 'EPSG:3857' }),
            tileGrid: null,
            maxZoom: 4,
            center: [0, 0],
        };
    }

    const zoomLevels: number[] = Object.keys(metadata.levels)
        .map(level => Number(level))
        .filter(level => !Number.isNaN(level))
        .sort((a, b) => a - b);

    if (!zoomLevels.length) {
        return {
            projection: new Projection({ code: 'EPSG:3857' }),
            tileGrid: null,
            maxZoom: 4,
            center: [0, 0],
        };
    }

    const maxZoom: number = zoomLevels[zoomLevels.length - 1];
    const maxLevel: PdfTileLevel = metadata.levels[String(maxZoom)];
    const extent: [number, number, number, number] = [0, -maxLevel.height, maxLevel.width, 0];
    const projection: Projection = new Projection({
        code: 'PDF_PIXELS',
        units: 'pixels',
        extent,
        global: false,
    });
    const resolutions: number[] = Array.from({ length: maxZoom + 1 }, (_, z) => 2 ** (maxZoom - z));
    const tileGrid: TileGrid = new TileGrid({
        extent,
        origin: [0, 0],
        tileSize: 256,
        resolutions,
    });

    return {
        projection,
        tileGrid,
        maxZoom,
        center: [maxLevel.width / 2, -maxLevel.height / 2],
    };
};

const getBoundsFromTileUrl = (tileUrl: string): [number, number, number, number] | null => {
    try {
        const url = new URL(tileUrl, window.location.origin);
        const boundsParam = url.searchParams.get('bounds') || url.searchParams.get('bbox') || url.searchParams.get('extent');

        if (!boundsParam) {
            return null;
        }

        const parts = boundsParam.split(',').map(v => Number(v.trim()));
        if (parts.length !== 4 || parts.some(v => Number.isNaN(v))) {
            return null;
        }

        return [parts[0], parts[1], parts[2], parts[3]];
    } catch {
        return null;
    }
};

const ROTATION_STEP_RAD = Math.PI / 2;

export const PdfRasterViewer: React.FC<PdfRasterViewerProps> = ({ tileUrl, height }) => {
    const mapElRef = React.useRef<HTMLDivElement | null>(null);
    const mapRef = React.useRef<Map | null>(null);
    const projectionRef = React.useRef<Projection | null>(null);
    const currentMaxZoomRef = React.useRef(4);
    // Monotonic init sequence id used to ignore stale async init results.
    // Each tileUrl change starts a new init run and bumps this token.
    const initTokenRef = React.useRef(0);
    const [rotationRad, setRotationRad] = React.useState(0);

    const clearMap = React.useCallback(() => {
        mapRef.current?.setTarget(undefined);
        mapRef.current = null;
        projectionRef.current = null;
    }, []);

    const fitToPdfBounds = React.useCallback(() => {
        try {
            const map: Map | null = mapRef.current;
            if (!map) {
                return;
            }

            const bounds = getBoundsFromTileUrl(tileUrl);
            if (!bounds) {
                const projectionExtent = projectionRef.current?.getExtent();

                if (projectionExtent) {
                    map.getView().fit(projectionExtent, {
                        size: map.getSize(),
                    });
                    return;
                }
            }
            map.getView().setCenter([0, 0]);
            map.getView().setZoom(0);
        } catch {
            mapRef.current?.getView().setCenter([0, 0]);
            mapRef.current?.getView().setZoom(0);
        }
    }, [tileUrl]);

    React.useEffect(() => {
        let cancelled = false;
        // Capture token for this specific init run.
        const initToken = initTokenRef.current + 1;
        initTokenRef.current = initToken;

        const initMap = async () => {
            const metadata = await fetchTileMetadata(tileUrl);
            // Ignore stale runs (older token), unmounted runs (cancelled),
            // and cases where target element no longer exists.
            if (cancelled || initToken !== initTokenRef.current || !mapElRef.current) {
                return;
            }

            const rasterConfig = buildRasterConfig(metadata);
            currentMaxZoomRef.current = rasterConfig.maxZoom;
            projectionRef.current = rasterConfig.projection;

            const source = new XYZ({
                url: tileUrl,
                crossOrigin: 'anonymous',
                projection: rasterConfig.projection,
                tileGrid: rasterConfig.tileGrid,
                maxZoom: rasterConfig.maxZoom,
                wrapX: false,
                tileLoadFunction: async (imageTile, src) => {
                    await tileLoadFunction({ imageTile, src, providerType: 'infrakitProvider', uuid: null, authToken: null });
                },
            });

            const layer = new TileLayer({ source });

            const map = new Map({
                target: mapElRef.current,
                layers: [layer],
                moveTolerance: 2,
                view: new View({
                    projection: rasterConfig.projection,
                    center: rasterConfig.center,
                    zoom: 2,
                    minZoom: 1,
                    maxZoom: 7,
                    enableRotation: true,
                }),
            });

            mapRef.current = map;
            fitToPdfBounds();
        };

        clearMap();
        void initMap();

        return () => {
            cancelled = true;
            // Invalidate any async work started by this run.
            initTokenRef.current += 1;
            clearMap();
        };
    }, [tileUrl, clearMap, fitToPdfBounds]);

    const handleRotate = React.useCallback(() => {
        const nextRotation = rotationRad + ROTATION_STEP_RAD;
        setRotationRad(nextRotation);
        mapRef.current?.getView().setRotation(nextRotation);
    }, [rotationRad]);

    return (
        <div style={{ position: 'relative', width: '100%', height: height ?? '100%', minHeight: 300 }}>
            <div ref={mapElRef} style={{ width: '100%', height: '100%' }} />
            <button
                onClick={handleRotate}
                title='Rotate 90°'
                aria-label='Rotate 90°'
                style={{
                    position: 'absolute',
                    top: 'var(--ik-space-s)',
                    right: 'var(--ik-space-s)',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    background: 'var(--ik-color-surface-default)',
                    border: '1px solid var(--ik-color-border-default)',
                    borderRadius: 'var(--ik-shape-br-m)',
                    cursor: 'pointer',
                    color: 'var(--ik-color-text-default)',
                    font: 'var(--ik-font-default-m)',
                }}
            >
                <i className='iki iki-rotate' aria-hidden='true' />
            </button>
        </div>
    );
};
